import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatAmount } from "../../utils/format";
import { secureGet } from "../../config/api";
import SnackbarNotification from '../../components/SnackbarNotification';

import xIcon from "../../icons/x.svg";
import noImage from '../../icons/noImageIcon.svg';

const ProductPopup = ({ productId, onClose }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [product, setProduct] = useState({});
  const { t } = useTranslation();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    if (!productId) return;
  
    const abortController = new AbortController();
    const fetchProduct = async () => {
      try {
        const product = await secureGet(`/products/${productId}`, showSnackbar, { signal: abortController.signal });
        setProduct(product.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching product:", error);
        }
      }
    };
  
    fetchProduct();
    return () => {
      abortController.abort();
    };
  }, [productId]);
  

  if (!productId) return null;

  const { image, name, priceTTC, reference, familyName, availability, discounts, discount, fields, salesDiscountLimit, family, priceConcessionTTC } = product;


  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "50px";
    e.target.style.height = "50px";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
      <div className="bg-[#F9FAFC] shadow-[0px_20px_80px_#00000011] rounded-lg w-[1052px] h-[550px] flex p-4 relative items-center">
        <button
          onClick={() => {
            onClose();
          }}
          className="absolute top-2 right-2 bg-[#FAFAFA] rounded-[24px] p-[5px]"
        >
          <img src={xIcon} alt={name} className="" />
          </button>
        <div
          className="flex items-center shadow-[0px_6px_26px_rgba(98,107,125,0.07)] bg-white justify-center overflow-hidden rounded-md z-10"
          style={{
            width: "45%",
            height: "98%",
            flexShrink: 0,
          }}
        >
          <img
            src={image}
            alt={name}
            className="object-contain w-full h-full"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
            }}
            onError={handleImageError
            }
          />
        </div>

        <div className="flex-1  overflow-hidden">
          <div className=" bg-white pl-8 pt-4 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
            <div
              className=" text-white  text-[16px] font-semibold py-1 px-4 rounded-full inline-block mb-2"
              style={{ backgroundColor: family?.color || '#6B78F8' }} 
                       >
              {familyName}
            </div>
            <h2 className="text-[26px] font-semibold text-gray-800">{name}</h2>
            <p className="text-[38px] font-bold text-[#6B78F8]">{formatAmount(priceTTC)}
            </p>
            {priceConcessionTTC != null && (
              <>
                <h2 className="text-[12px] font-semibold text-[#707070] mb-[-10px]">{t(`fields.products.priceConcessionTTC`).toUpperCase()}</h2>
                <p className="text-[29px] font-bold text-[#6B78F8]">{formatAmount(priceConcessionTTC)}</p>
              </>
            )}

            <div className="mt-2">
              {/* Titles */}
              <div className="flex items-center text-[12px] font-semibold text-[#707070]">
                <span className="mr-4">{t(`rows.discountApplied`)}:</span>
                <span className="ml-8">{t(`rows.maxDiscount`)}:</span>
              </div>

              {/* Values */}
              <div className="flex items-center mt-1 text-[#020040] text-[20px] font-semibold">
                <span className="mr-16">{discount || "0.00"}%</span>
                <span className="ml-12">{salesDiscountLimit || "0.00"}%</span>
              </div>
            </div>
          <div className="flex text-[12px] space-x-6 mt-[16px] ">
            <button
              className={`pb-1 px-4 ${activeTab === "details"
                ? "text-[#27469B] font-semibold border-b-2 border-[#27469B]"
                : "text-[#ACAFC6] font-semibold "
                }`}
              onClick={() => handleTabChange("details")}
            >
              {t(`rows.details`)}
            </button>
            <button
              className={`pb-1 px-4 ${activeTab === "availability"
                ? "text-[#27469B] font-semibold border-b-2 border-[#27469B]"
                : "text-[#ACAFC6] font-semibold"
                }`}
              onClick={() => handleTabChange("availability")}
            >
              {t(`rows.availability`)}
            </button>
            <button
              className={`pb-1 px-4 ${activeTab === "discounts"
                ? "text-[#27469B] font-semibold border-b-2 border-[#27469B]"
                : "text-[#ACAFC6] font-semibold"
                }`}
              onClick={() => handleTabChange("discounts")}
            >
              {t(`rows.discount`)}
            </button>
          </div>
          </div>


          <div className=" overflow-y-auto h-[240px] pt-[18px] pl-8">
            {activeTab === "details" && (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 items-start text-[#707070] mt-2 mb-4 border-b border-gray-200 pb-2">
                  <span className="text-left font-medium text-[16px] text-[#707070]">
                    {t(`rows.reference`)}
                  </span>
                  <span className="text-[#020040] font-semibold">{reference}</span>
                </div>

                {fields &&
                  fields.map((field, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-2 gap-y-2 items-start text-[#707070] mt-2 mb-4 border-b border-gray-200 pb-2"
                    >
                      <span className="text-left font-medium text-[16px] text-[#707070]">
                        {field.name}
                      </span>
                      <span className="text-[#020040] font-semibold">{field.value}</span>
                    </div>
                  ))}
              </div>
            )}


            {activeTab === "availability" && (
              <div>
                {availability && availability.length > 0 ? (
                  <div
                    className=""
                    style={{
                      maxHeight: "220px",
                      overflowY: "auto",
                    }}
                  >
                    {availability.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-[300px_100px_100px] items-center border-b border-gray-200 text-[16px] mb-2 py-2"
                      >
                        <span className="w-[300px] font-medium text-[16px] text-[#707070] whitespace-normal">
                          {item.repoName}
                        </span>

                        <span className="w-[100px] text-left text-[#020040] font-semibold">
                          Q: {item.quantity}
                        </span>

                        <span className="w-[100px] text-left text-[#020040] font-semibold">
                          R: {item.reserved}
                        </span>
                      </div>

                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600"></p>
                )}
              </div>
            )}


            {activeTab === "discounts" && (
              <div>
                {discounts && discounts.length > 0 ? (
                  <table className="w-full text-left">
                    <tbody>
                      {discounts.map((discount, index) => (
                        <tr key={index} className="border-b border-gray-200 ">
                          <td className="p-2 font-medium text-[16px] text-[#707070] w-1/2">
                            {discount.quantity} +
                          </td>
                          <td className=" p-2 text-[#020040] text-[16px] font-semibold w-1/2">
                            {formatAmount(discount.price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-600"></p>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>

  );
};

export default ProductPopup;
